/**
 * The "Invite an SI" panel's stylesheet. Every accent is the organization's own brand colour from its theme
 * (`--color-primary`, injected by branding/overrides.js from the org's globals.css), never a fixed blue; the fallback
 * is a neutral slate. Semantic colours only for state (available, busy, failed). Every animation and transition stops
 * under prefers-reduced-motion.
 */
export const SI_INVITE_CSS = `
.ksi-panel {
  --ksi-accent: var(--color-primary, #5b6577);
  --ksi-accent-strong: var(--color-primary-dark, var(--color-primary, #485163));
  --ksi-accent-soft: color-mix(in srgb, var(--ksi-accent) 22%, transparent);
  --ksi-on-accent: #ffffff;
  --ksi-surface: rgba(20, 22, 28, 0.94);
  --ksi-card: rgba(255, 255, 255, 0.045);
  --ksi-card-hover: rgba(255, 255, 255, 0.08);
  --ksi-line: rgba(255, 255, 255, 0.09);
  --ksi-text: #f3f4f6;
  --ksi-text-2: #c4c8d0;
  --ksi-text-3: #9aa1ad;
  --ksi-ok: #34d399;
  --ksi-busy: #fbbf24;
  --ksi-idle: #a3acb9;
  --ksi-bad: #fca5a5;
  position: fixed;
  right: 16px;
  bottom: 88px;
  z-index: 300;
  width: 392px;
  max-width: calc(100vw - 32px);
  max-height: min(640px, calc(100vh - 120px));
  display: flex;
  flex-direction: column;
  color: var(--ksi-text);
  background: var(--ksi-surface);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
  backdrop-filter: blur(20px) saturate(140%);
  border: 1px solid var(--ksi-line);
  border-radius: 20px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(0, 0, 0, 0.25);
  font-family: inherit;
  overflow: hidden;
  outline: none;
  transform-origin: bottom right;
  animation: ksi-pop 260ms cubic-bezier(0.2, 0.9, 0.25, 1.15) both;
}
.ksi-panel::before {
  content: "";
  position: absolute;
  inset: -40% -20% auto auto;
  width: 280px;
  height: 280px;
  background: radial-gradient(circle at center, var(--ksi-accent-soft), transparent 65%);
  pointer-events: none;
}
@keyframes ksi-pop {
  from { opacity: 0; transform: translateY(14px) scale(0.96); }
  to { opacity: 1; transform: none; }
}
.ksi-head { position: relative; display: flex; gap: 12px; align-items: flex-start; padding: 18px 18px 12px; }
.ksi-badge {
  flex: none; width: 40px; height: 40px; border-radius: 12px;
  display: grid; place-items: center; color: var(--ksi-on-accent);
  background: linear-gradient(140deg, var(--ksi-accent), var(--ksi-accent-strong));
  box-shadow: 0 6px 18px var(--ksi-accent-soft);
}
.ksi-badge svg { fill: none; stroke: currentColor; }
.ksi-head-text { flex: 1; min-width: 0; }
.ksi-heading { margin: 0; font-size: 17px; line-height: 22px; font-weight: 650; letter-spacing: -0.01em; }
.ksi-sub { margin: 3px 0 0; font-size: 13px; line-height: 18px; color: var(--ksi-text-2); }
.ksi-close {
  flex: none; width: 34px; height: 34px; border-radius: 10px; border: 0; cursor: pointer;
  display: grid; place-items: center; color: var(--ksi-text-2); background: transparent;
  transition: background 140ms ease, color 140ms ease;
}
.ksi-close:hover { background: var(--ksi-card-hover); color: var(--ksi-text); }
.ksi-close svg { fill: currentColor; }
.ksi-search {
  position: relative; display: flex; align-items: center; gap: 8px; margin: 0 18px 8px; padding: 0 12px;
  height: 38px; border-radius: 11px; background: var(--ksi-card); border: 1px solid var(--ksi-line);
  color: var(--ksi-text-3); transition: border-color 140ms ease, box-shadow 140ms ease;
}
.ksi-search:focus-within { border-color: var(--ksi-accent); box-shadow: 0 0 0 3px var(--ksi-accent-soft); }
.ksi-search svg { fill: currentColor; }
.ksi-search input {
  flex: 1; min-width: 0; height: 100%; border: 0; outline: none; background: transparent;
  color: var(--ksi-text); font: inherit; font-size: 14px;
}
.ksi-search input::placeholder { color: var(--ksi-text-3); }
.ksi-body { position: relative; flex: 1; overflow-y: auto; padding: 4px 10px 8px; scrollbar-width: thin; }
.ksi-group-head { display: flex; align-items: center; gap: 8px; padding: 10px 8px 0; }
.ksi-group-title {
  margin: 0; font-size: 11.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--ksi-text-2);
}
.ksi-count {
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px; display: inline-grid; place-items: center;
  font-size: 11.5px; font-weight: 700; color: var(--ksi-text); background: var(--ksi-accent-soft);
}
.ksi-group-hint { margin: 3px 8px 8px; font-size: 12.5px; line-height: 17px; color: var(--ksi-text-3); }
.ksi-list { list-style: none; margin: 0 0 6px; padding: 0; display: grid; gap: 6px; }
.ksi-card {
  border-radius: 14px; background: var(--ksi-card); border: 1px solid transparent;
  transition: background 160ms ease, border-color 160ms ease, transform 160ms ease;
  animation: ksi-rise 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.ksi-card:hover { background: var(--ksi-card-hover); border-color: var(--ksi-line); }
.ksi-phase-joining, .ksi-phase-inviting { border-color: var(--ksi-accent-soft); }
.ksi-phase-joined { border-color: color-mix(in srgb, var(--ksi-ok) 40%, transparent); background: color-mix(in srgb, var(--ksi-ok) 10%, transparent); }
.ksi-phase-failed { border-color: color-mix(in srgb, var(--ksi-bad) 35%, transparent); }
@keyframes ksi-rise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.ksi-row { display: flex; align-items: center; gap: 12px; padding: 10px 10px 10px 10px; }
.ksi-face { position: relative; flex: none; width: 44px; height: 44px; border-radius: 50%; }
.ksi-face .avatar { width: 44px !important; height: 44px !important; }
.ksi-face-sm { width: 28px; height: 28px; }
.ksi-face-sm .avatar { width: 28px !important; height: 28px !important; }
.ksi-face-busy::after {
  content: ""; position: absolute; inset: -4px; border-radius: 50%;
  background: conic-gradient(from 0deg, var(--ksi-accent), transparent 70%, var(--ksi-accent));
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px));
  animation: ksi-spin 1.1s linear infinite;
}
@keyframes ksi-spin { to { transform: rotate(360deg); } }
.ksi-dot {
  position: absolute; right: -1px; bottom: -1px; width: 12px; height: 12px; border-radius: 50%;
  border: 2px solid #16181d; background: var(--ksi-idle);
}
.ksi-tone-ok { background: var(--ksi-ok); }
.ksi-tone-busy { background: var(--ksi-busy); }
.ksi-tone-idle { background: var(--ksi-idle); }
.ksi-tone-muted { background: #6b7280; }
.ksi-check {
  position: absolute; inset: 0; border-radius: 50%; display: grid; place-items: center;
  background: color-mix(in srgb, var(--ksi-ok) 82%, #000); color: #052e1c;
  animation: ksi-check 360ms cubic-bezier(0.2, 0.9, 0.3, 1.4) both;
}
.ksi-check svg, .ksi-joined-pill svg { fill: currentColor; }
@keyframes ksi-check { from { opacity: 0; transform: scale(0.4); } to { opacity: 1; transform: none; } }
.ksi-who { flex: 1; min-width: 0; }
.ksi-name { font-size: 14.5px; font-weight: 620; line-height: 19px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ksi-title { font-size: 12.5px; line-height: 17px; color: var(--ksi-text-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ksi-status { margin-top: 2px; font-size: 12px; line-height: 16px; }
.ksi-tone-text-ok { color: var(--ksi-ok); }
.ksi-tone-text-busy { color: var(--ksi-busy); }
.ksi-tone-text-idle { color: var(--ksi-text-3); }
.ksi-tone-text-muted { color: var(--ksi-text-3); }
.ksi-action { flex: none; }
.ksi-btn {
  position: relative; display: inline-flex; align-items: center; gap: 7px; height: 34px; padding: 0 14px;
  border-radius: 10px; border: 0; cursor: pointer; font: inherit; font-size: 13.5px; font-weight: 650;
  color: var(--ksi-on-accent); background: var(--ksi-accent);
  box-shadow: 0 4px 14px var(--ksi-accent-soft), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  transition: transform 120ms ease, background 140ms ease, box-shadow 140ms ease, opacity 140ms ease;
}
.ksi-btn:hover:not(:disabled) { background: var(--ksi-accent-strong); transform: translateY(-1px); }
.ksi-btn:active:not(:disabled) { transform: translateY(0) scale(0.98); }
.ksi-btn:focus-visible, .ksi-close:focus-visible, .ksi-link:focus-visible {
  outline: 2px solid var(--ksi-accent); outline-offset: 2px;
}
.ksi-btn:disabled { cursor: default; opacity: 0.55; box-shadow: none; }
.ksi-btn-busy:disabled { opacity: 1; background: var(--ksi-accent-soft); color: var(--ksi-text); }
.ksi-spinner {
  width: 13px; height: 13px; border-radius: 50%; border: 2px solid currentColor; border-right-color: transparent;
  animation: ksi-spin 0.8s linear infinite;
}
.ksi-joined-pill {
  display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 12px; border-radius: 999px;
  font-size: 13px; font-weight: 650; color: var(--ksi-ok); background: color-mix(in srgb, var(--ksi-ok) 14%, transparent);
}
.ksi-unavailable-pill {
  display: inline-flex; align-items: center; height: 30px; padding: 0 12px; border-radius: 999px;
  font-size: 12.5px; font-weight: 600; color: var(--ksi-text-3); border: 1px solid var(--ksi-line);
}
.ksi-locked { background: transparent; border: 1px dashed var(--ksi-line); }
.ksi-locked .ksi-face { opacity: 0.7; }
.ksi-locked-why { margin: -4px 12px 10px 66px; font-size: 12.5px; line-height: 17px; color: var(--ksi-text-2); }
.ksi-failure {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin: -2px 10px 10px 66px;
  font-size: 12.5px; line-height: 17px; color: var(--ksi-bad);
}
.ksi-link {
  flex: none; border: 0; padding: 0; background: none; cursor: pointer; font: inherit; font-size: 12.5px;
  color: var(--ksi-text-2); text-decoration: underline; text-underline-offset: 2px;
}
.ksi-present { margin: 8px 0 4px; padding: 10px 8px 4px; border-top: 1px solid var(--ksi-line); }
.ksi-present-list { list-style: none; margin: 8px 0 0; padding: 0; display: grid; gap: 8px; }
.ksi-present-item { display: flex; align-items: center; gap: 10px; font-size: 13px; }
.ksi-present-name { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--ksi-text); }
.ksi-present-state { flex: none; font-size: 12px; }
.ksi-empty { display: grid; justify-items: center; text-align: center; gap: 6px; padding: 28px 22px 22px; }
.ksi-empty-art {
  width: 56px; height: 56px; border-radius: 18px; display: grid; place-items: center; margin-bottom: 4px;
  color: var(--ksi-text); background: var(--ksi-accent-soft);
}
.ksi-empty-art svg { fill: none; stroke: currentColor; }
.ksi-empty-title { margin: 0; font-size: 14.5px; font-weight: 620; }
.ksi-empty-hint { margin: 0; font-size: 13px; line-height: 18px; color: var(--ksi-text-2); max-width: 290px; }
.ksi-soft-error { margin: 8px; font-size: 12.5px; color: var(--ksi-busy); }
.ksi-skeleton .ksi-row { gap: 12px; }
.ksi-sk-face, .ksi-sk-line {
  background: linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.14), rgba(255,255,255,0.06));
  background-size: 200% 100%; animation: ksi-shimmer 1.3s ease-in-out infinite;
}
.ksi-sk-face { width: 44px; height: 44px; border-radius: 50%; flex: none; }
.ksi-sk-line { height: 11px; border-radius: 6px; width: 70%; margin: 6px 0; }
.ksi-sk-short { width: 42%; }
@keyframes ksi-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
.ksi-foot {
  padding: 10px 18px 14px; font-size: 12px; line-height: 16px; color: var(--ksi-text-3);
  border-top: 1px solid var(--ksi-line);
}
@media (max-width: 600px) {
  .ksi-panel {
    left: 12px; right: 12px; bottom: 76px; width: auto; max-width: none;
    max-height: calc(100vh - 100px); border-radius: 18px; transform-origin: bottom center;
  }
  .ksi-btn { height: 38px; padding: 0 14px; }
}
@media (prefers-reduced-motion: reduce) {
  .ksi-panel, .ksi-card, .ksi-check, .ksi-face-busy::after, .ksi-spinner, .ksi-sk-face, .ksi-sk-line {
    animation: none !important;
  }
  .ksi-card, .ksi-btn, .ksi-close, .ksi-search { transition: none !important; }
  .ksi-btn:hover:not(:disabled) { transform: none; }
}
`;
